// A new version of the application, downloaded and waiting (INC-13, B-24).
//
// The shell is precached and a new build waits: the service worker that
// carries it installs beside the one in charge and waits until every tab of
// the application is closed, or until the person asks for it. This holds
// whether it is waiting, for the notice the three experiences show under
// their header, and what pressing "Actualizar" does: what the device was
// writing reaches IndexedDB first, then the new worker takes charge and the
// page reloads where it was. Nothing is forced: ignored, the notice stays, and
// the update happens on its own once every tab is closed, as it always did.
import { useSyncExternalStore } from 'react';
import { db } from '../db/database';

let ready = false;
const listeners = new Set<() => void>();

/** The new version is downloaded and waits. */
export function markUpdateReady(): void {
  ready = true;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Whether a new version waits for the person. */
export function useUpdateReady(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => ready,
    () => false,
  );
}

/** The writes the device has started reach IndexedDB before the page goes: a
 *  transaction that reads the stores of the work waits for every one that
 *  writes them and began before it. Then the waiting worker is told to take
 *  charge, and once it controls the page, the page reloads at the same
 *  address — a visit open is opened again.
 *
 *  The worker is told here and not through the registration helper: that one
 *  reloads only for a version its own registration found, in the first instant
 *  of the page's life, and a tab open for hours finds it much later. */
export async function applyUpdate(): Promise<void> {
  if (!ready) return;
  await db.transaction(
    'r',
    [db.session, db.visit_drafts, db.outbox_data, db.photo_queue, db.sync_log],
    () => undefined,
  );
  const registration = await navigator.serviceWorker.getRegistration();
  const waiting = registration?.waiting;
  if (waiting === undefined || waiting === null) return;
  navigator.serviceWorker.addEventListener(
    'controllerchange',
    () => {
      window.location.reload();
    },
    { once: true },
  );
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

// How often a tab that stays open asks whether a new version was published,
// besides what the browser asks on its own when the application starts.
export const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/** The registration of the service worker asked for a new version every hour,
 *  and when the tab comes back after being hidden for a while. */
export function watchForUpdates(registration: ServiceWorkerRegistration): void {
  let lastCheck = Date.now();
  const check = (): void => {
    if (!navigator.onLine) return;
    lastCheck = Date.now();
    registration.update().catch(() => undefined);
  };
  setInterval(check, UPDATE_CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (
      document.visibilityState === 'visible' &&
      Date.now() - lastCheck > UPDATE_CHECK_INTERVAL_MS / 4
    )
      check();
  });
}
